<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { captureException } from '@@/bits/error_tracker'
import { defineAsyncComponent } from '@@/bits/vue'
import {
  WHITEBOARD_ATTACHMENT_PADDING,
  shouldEnableWhiteboardInPlaceUploadingAnimation,
} from '@@/bits/whiteboard_no_dependencies'
import { whiteboardAudioPlayerV2Key } from '@@/bits/whiteboard_provide_inject_keys'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMediaPlayerStore } from '@@/pinia/surface_media_player'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import type { Post } from '@@/types'
import SurfacePostAttachment from '@@/vuecomponents/SurfacePostAttachment.vue'
import { vPointerdownStop } from '@@/vuedirectives/stop_pointerdown_propagation'
import { autoPlacement, offset } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { debounce, memoize } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type { TLShapeId } from 'tldraw'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'

const OzBasePopover = defineAsyncComponent(() => import('@@/library/v4/components/OzBasePopover.vue'))
const SurfacePostAttachmentEmbedControl = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostAttachmentEmbedControl.vue'),
)
const SurfacePostPoll = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostPoll.vue'))
const WhiteboardAttachmentCornerActionButton = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardAttachmentCornerActionButton.vue'),
)
const WhiteboardAttachmentLoading = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardAttachmentLoading.vue'),
)

const props = defineProps<{
  postId: number
  width: number
  height: number
  uploadKey?: string
  xAudioPlayerV2?: boolean
  shapeId: TLShapeId
}>()

const emit = defineEmits<{
  (e: 'loaded'): void
  (e: 'errored'): void
}>()

// Don't want to prop-drill xAudioPlayerV2 through the child components since they are shared by both
// Board attachments and Sandbox ones.
provide(whiteboardAudioPlayerV2Key, props.xAudioPlayerV2)

// Data are set by parent component
const attachmentWidth = ref(props.width)

const componentRef = ref<InstanceType<typeof SurfacePostAttachment> | null>(null)
const $el = computed(() => componentRef.value?.$el as HTMLElement | undefined)

function setWidthHeightAttrAndStyle(el: HTMLElement, dimensions: { width: number; height: number }): void {
  el.setAttribute('width', dimensions.width.toString())
  el.setAttribute('height', dimensions.height.toString())
  el.style.width = `${dimensions.width}px`
  el.style.height = `${dimensions.height}px`
}

const getImageAr = memoize(
  (imageThumbnailImageEl: HTMLImageElement) =>
    parseFloat(imageThumbnailImageEl.getAttribute('width') ?? '1.0') /
    parseFloat(imageThumbnailImageEl.getAttribute('height') ?? '1.0'),
  {
    getCacheKey: (imageThumbnailImageEl: HTMLImageElement) => imageThumbnailImageEl.src,
  },
)
/**
 * Update the attachment size while resizing the component. This is done in our old Canvas when a post is resized.
 * Replicated the logic in resizable_mixin.js
 */
function updateAttachmentSizeWhileResizing(): void {
  const previewAspectRatio = parseFloat(
    $el.value?.querySelector('[data-aspect-ratio]')?.getAttribute('data-aspect-ratio') as string,
  )
  if (!previewAspectRatio) {
    return
  }
  const imageGridNumOfImages = parseInt(
    $el.value?.querySelector('.image-grid-container')?.getAttribute('data-image-grid-num-of-images') as string,
    10,
  )
  const usingImageGrid = imageGridNumOfImages > 0

  const postV2Dimensions = {
    width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
    height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / previewAspectRatio),
  }

  // $('.surface-post-attachment-inline-embed iframe', this.$el).attr(postV2Dimensions).css(postV2Dimensions)
  // $('.image-thumbnail-crop-frame,.image-thumbnail-image', this.$el).attr(postV2Dimensions).css(postV2Dimensions)

  const iframeEl = $el.value?.querySelector('.surface-post-attachment-inline-embed iframe') as
    | HTMLIFrameElement
    | undefined
  if (iframeEl) {
    setWidthHeightAttrAndStyle(iframeEl, postV2Dimensions)
  }

  if (usingImageGrid) {
    const imageGridAspectRatio = parseFloat(
      $el.value?.querySelector('.image-grid-container')?.getAttribute('data-image-grid-aspect-ratio') as string,
    )
    // Post View v2 with image grid: we only resize the grid container as the images in the grid always
    // expand to fill the container
    // When the grid has one image, we use that image's aspect ratio
    const ar = imageGridNumOfImages === 1 ? previewAspectRatio : imageGridAspectRatio
    const imageGridDimensions = {
      width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
      height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / ar),
    }

    // $('.image-grid-container', this.$el).attr(imageGridDimensions).css(imageGridDimensions)
    const imageGridContainerEl = $el.value?.querySelector('.image-grid-container') as HTMLElement | undefined
    if (imageGridContainerEl) {
      setWidthHeightAttrAndStyle(imageGridContainerEl, imageGridDimensions)
    }
  } else {
    const imageThumbnailCropFrameEl = $el.value?.querySelector('.image-thumbnail-crop-frame') as HTMLElement | undefined
    const imageThumbnailImageEl = $el.value?.querySelector('.image-thumbnail-image') as HTMLImageElement | undefined

    if (!imageThumbnailCropFrameEl && !imageThumbnailImageEl) {
      // console.warn('WTF: No image-thumbnail-crop-frame or image-thumbnail-image found in the attachment')
    }
    if (imageThumbnailCropFrameEl) {
      setWidthHeightAttrAndStyle(imageThumbnailCropFrameEl, postV2Dimensions)
    }
    if (imageThumbnailImageEl) {
      const imageDimensions = {
        width: props.width - WHITEBOARD_ATTACHMENT_PADDING * 2,
        height: Math.floor((props.width - WHITEBOARD_ATTACHMENT_PADDING * 2) / getImageAr(imageThumbnailImageEl)),
      }
      setWidthHeightAttrAndStyle(imageThumbnailImageEl, imageDimensions)
    }
  }
}

// Update beenhoven attachment width when finish resizing
// New logic: update to the largest width that the image has ever been resized to
// This is for when the image is resized to a smaller width, we don't fetch the image again from Beethoven
const largestImgWidthRendered = ref(props.width)
function updateAttachmentWidth(newWidth): void {
  attachmentWidth.value = newWidth
}
const debouncedUpdateAttachmentWidth = debounce(updateAttachmentWidth, 1000, { edges: ['trailing'] })
watch(
  () => props.width,
  (newWidth) => {
    if (newWidth > largestImgWidthRendered.value) {
      largestImgWidthRendered.value = newWidth
      debouncedUpdateAttachmentWidth(largestImgWidthRendered.value)
    }
    updateAttachmentSizeWhileResizing()
  },
  { immediate: true, flush: 'post' },
)

const displayAttributes = ref<BeethovenDisplayAttributes | null>(null)
function handleImageLoaded(data: BeethovenDisplayAttributes): void {
  displayAttributes.value = data
  nextTick(() => {
    try {
      const img = $el.value?.querySelector('img') as HTMLImageElement & { __loadedHandled?: boolean }
      if (!$el.value) {
        throw new Error('Preview component not found')
      }
      if (!img) {
        throw new Error('Image element not found')
      }
      updateAttachmentSizeWhileResizing()

      // Here the img re-render. We need to update the attachment size again
      if (!img.__loadedHandled) {
        img.addEventListener('load', () => {
          emit('loaded')
          updateAttachmentSizeWhileResizing()
        })
        img.addEventListener('error', () => {
          emit('errored')
          updateAttachmentSizeWhileResizing()
        })
        img.__loadedHandled = true
      }
    } catch (error) {
      captureException(error, {
        context: {
          postId: props.postId,
          displayAttributes: data,
        },
      })
    }
  })
}
const surfacePostsStore = useSurfacePostsStore()
const post = computed<Post | undefined>(() => surfacePostsStore.postEntitiesById[props.postId])

const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIAccessContentDirectly } = storeToRefs(surfacePermissionsStore)

function toggleEmbedControl(): void {
  // TODO
}

const xPoll = computed(() => post.value?.wish_content?.attachment_props?.type === 'poll')
const poll = computed(() => (xPoll.value ? post.value?.wish_content?.attachment_props : null))

const surfaceStore = useSurfaceStore()
const { user } = storeToRefs(surfaceStore)
const displayedPostColor = null

const surfaceMediaPlayerStore = useSurfaceMediaPlayerStore()
const expandedPostStore = useExpandedPostStore()
function expand(): void {
  if (post.value?.cid) {
    expandedPostStore.expandPost({ postCid: post.value.cid, xTextPanel: false })
  }
}
const isPostPlayingMedia = ref(false)

// #region Uploading animation

const whiteboardUploadStore = useWhiteboardUploadStore()

const upload = computed(() => {
  return props.uploadKey ? whiteboardUploadStore.uploads[props.uploadKey] : null
})
const isUploadingFile = computed(() => upload.value?.progress != null && typeof upload.value.progress === 'number')
const isUploading = computed(
  () => shouldEnableWhiteboardInPlaceUploadingAnimation() && upload.value && props.uploadKey && props.postId === 0,
)
const isUploadingAndTrimmingVideo = computed(() => {
  return upload.value?.uploadJob?.shouldTrimVideoAfterUpload
})

// #endregion

watch(
  () => surfaceMediaPlayerStore.currentlyPlayingPostCid,
  (newCurrentlyPlayingPostCid) => {
    if (newCurrentlyPlayingPostCid === post.value?.cid) {
      isPostPlayingMedia.value = surfaceMediaPlayerStore.isPostPlayingMedia(post.value?.cid)
      nextTick(() => {
        updateAttachmentSizeWhileResizing()
      })
    } else {
      isPostPlayingMedia.value = false
    }
  },
)

watch(
  () => useWhiteboardStore().currentFrameId,
  () => {
    componentRef.value?.postMessage('back-to-preview')
    surfaceMediaPlayerStore.unsetCurrentlyPlayingMedia()
  },
)

onMounted(() => {
  window.addEventListener('visibilitychange', updateAttachmentSizeWhileResizing)
})
onUnmounted(() => {
  window.removeEventListener('visibilitychange', updateAttachmentSizeWhileResizing)
  if (surfaceMediaPlayerStore.currentlyPlayingPostCid === post.value?.cid) {
    surfaceMediaPlayerStore.unsetCurrentlyPlayingMedia()
  }
})
</script>

<template>
  <div
    v-pointerdown-stop
    :style="{ width: props.width + 'px', height: props.height + 'px', padding: WHITEBOARD_ATTACHMENT_PADDING + 'px' }"
    :class="[
      'relative',
      'flex',
      'items-center',
      'justify-center',
      'rounded-xl',
      {
        'bg-transparent': !xPoll,
        'bg-dark-ui-200': xPoll,
      },
      !isUploading && 'overflow-hidden',
    ]"
  >
    <WhiteboardAttachmentLoading
      v-if="isUploading"
      :dir="dir()"
      :is-uploading-file="isUploadingFile"
      :uploading-file="upload && upload.file != null ? upload.file : undefined"
      :file-uploading-progress="upload && upload.progress ? upload.progress : 0"
      :background-width="props.width"
      :background-height="props.height"
      :is-uploading-and-trimming-video="isUploadingAndTrimmingVideo"
      @cancel="whiteboardUploadStore.cancelUpload(uploadKey)"
    />
    <SurfacePostAttachment
      v-else-if="post && post.attachment"
      ref="componentRef"
      :key="post.attachment"
      class="flex-1 flex flex-col justify-center overflow-hidden"
      :style="{ width: props.width + 'px', height: props.height + 'px' }"
      :dir="dir()"
      :post="post"
      :displayed-post-height="props.height"
      :displayed-post-width="attachmentWidth"
      :is-editable-by-current-user="false"
      :override-color-scheme="'dark'"
      @toggle-embed-control="toggleEmbedControl"
      @contextmenu.prevent
      @data-loaded="handleImageLoaded"
    />
    <WhiteboardAttachmentCornerActionButton
      v-if="
        componentRef && displayAttributes && !(componentRef.interaction === 'download' && !canIAccessContentDirectly)
      "
      :post="post"
      :interaction="componentRef.interaction"
      :display-attributes="displayAttributes"
      :default-aria-label="componentRef.ariaLabel"
      :dir="dir()"
      @click.stop="componentRef.clickAnchorElement"
    />
    <Portal selector="body">
      <OzBasePopover
        v-if="componentRef && $el && isPostPlayingMedia"
        ref="embedControlRef"
        class="z-modal"
        :anchor="$el"
        :config="{
          middleware: [
            offset(4),
            autoPlacement({
              allowedPlacements: ['bottom', 'top'],
            }),
          ],
        }"
        :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
      >
        <SurfacePostAttachmentEmbedControl
          :dir="dir()"
          :color-scheme="surfaceStore.backgroundLuminance === 'dark' ? 'dark' : 'light'"
          :is-downloadable="false"
          :download-url="''"
          @back-to-preview="componentRef.postMessage('back-to-preview')"
          @zoom="componentRef.postMessage('zoom')"
          @expand="expand"
          @open-in-new="componentRef.postMessage('open-in-new')"
        />
      </OzBasePopover>
    </Portal>
    <SurfacePostPoll
      v-if="xPoll"
      :id="`whiteboard-poll-${props.shapeId}`"
      class="bg-dark-ui-200 pointer-events-auto"
      :poll="poll"
      :poll-caption="post.attachment_caption"
      :post="post"
      :user="user"
      :dark-mode="true"
      :background-color="displayedPostColor"
      :display-mode="'slideshow'"
    />
  </div>
</template>

<style lang="postcss" scoped>
/deep/ button {
  @apply cursor-pointer pointer-events-auto;
}
</style>
